import PageHeader from 'components/PageHeader'
import ProtectedRoute from 'components/ProtectedRoute'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useFlag from 'hooks/useFlag'
import useIsAdmin from 'hooks/useIsAdmin'
import { buildTranslate } from 'locales'
import React from 'react'
import { Container } from 'react-bootstrap-five'
import Helmet from 'react-helmet'
import { Redirect, Switch } from 'react-router-dom'
import Analytics from 'scenes/Cms/Settings/Analytics'
import CheckoutMessage from 'scenes/Cms/Settings/CheckoutMessage'
import DeliveryDefaultLogic from 'scenes/Cms/Settings/DeliveryDefaultLogic'
import EmailReplyRouting from 'scenes/Cms/Settings/EmailReplyRouting'
import InstoreEnrollment from 'scenes/Cms/Settings/InstoreEnrollment'
import InstorePurchaseTracking from 'scenes/Cms/Settings/InstorePurchaseTracking'
import OrderingConfiguration from 'scenes/Cms/Settings/OrderingConfiguration'
import PostOrderFeedback from 'scenes/Cms/Settings/PostOrderFeedback'
import { isPostOrderFeedbackHidden } from 'scenes/Cms/Settings/PostOrderFeedback/constants'
import PreferencesModalSetting from 'scenes/Cms/Settings/PreferencesModalSetting'
import PromoCodeVisibility from 'scenes/Cms/Settings/PromoCodeVisibility'
import ReceiptSettings from 'scenes/Cms/Settings/ReceiptSettings'
import RevenueCenters from 'scenes/Cms/Settings/RevenueCenters'
import ScannableCodes from 'scenes/Cms/Settings/ScannableCodes'
import TermsAndPolicies from 'scenes/Cms/Settings/TermsAndPolicies'
import TermsAndPoliciesBuilder from 'scenes/Cms/Settings/TermsAndPolicies/TermsAndPoliciesBuilder'
import TimeBasedMenuVisibility from 'scenes/Cms/Settings/TimeBasedMenuVisibility'
import UnavailableItemVisibility from 'scenes/Cms/Settings/UnavailableItemVisibility'
import ErrorMessages from 'scenes/OrderingManagement/ErrorMessages'

const t = buildTranslate('cms.settings_landing')
const settingsT = buildTranslate('cms.settings')

const pageComponents: Record<string, React.FC<{ showTitle?: boolean }>> = {
  ordering_configuration: OrderingConfiguration,
  unavailable_item_visibility: UnavailableItemVisibility,
  time_based_menu_visibility: TimeBasedMenuVisibility,
  delivery_default_logic: DeliveryDefaultLogic,
  preferences_modal_setting: PreferencesModalSetting,
  receipt_settings: ReceiptSettings,
  revenue_centers: RevenueCenters,
  ordering_error_messages: ErrorMessages,
  checkout_message: CheckoutMessage,
  scannable_codes: ScannableCodes,
  post_order_feedback: PostOrderFeedback,
  email_reply_routing: EmailReplyRouting,
  instore_enrollment: InstoreEnrollment,
  instore_purchase_tracking: InstorePurchaseTracking,
  terms_and_policies: TermsAndPolicies,
  analytics: Analytics,
  promo_code_visibility: PromoCodeVisibility,
}

const pageTitleKeys: Record<string, string> = {
  ordering_configuration: 'ordering_configuration.title',
  unavailable_item_visibility: 'unavailable_item_visibility.title',
  time_based_menu_visibility: 'time_based_menu_visibility.title',
  delivery_default_logic: 'delivery_default_logic.title',
  preferences_modal_setting: 'preferences_modal_setting.title',
  receipt_settings: 'receipt_settings.title',
  revenue_centers: 'revenue_centers.title',
  ordering_error_messages: 'ordering_error_messages.title',
  checkout_message: 'checkout_message.title',
  scannable_codes: 'scannable_codes.title',
  post_order_feedback: 'post_order_feedback.title',
  email_reply_routing: 'email_reply_routing.title',
  instore_enrollment: 'instore_enrollment.title',
  instore_purchase_tracking: 'instore_purchase_tracking.title',
  terms_and_policies: 'terms_and_policies.title',
  analytics: 'analytics.tab',
  promo_code_visibility: 'promo_code_visibility.title',
}

const featureFlagsByPage: Record<string, string[]> = {
  unavailable_item_visibility: ['mx-unavailable-item-visibility'],
  time_based_menu_visibility: ['olo-time-based-menu-visibility-mx'],
  delivery_default_logic: ['delivery-optimization-mx'],
  terms_and_policies: ['mx-customizable-legal-docs'],
  instore_enrollment: ['instore-enrollment-settings'],
  ordering_error_messages: [
    'ordering-error-message-customization',
    'mx-4-ordering-management',
  ],
  revenue_centers: ['toast-revenue-center-enabled-mx'],
  promo_code_visibility: ['promo-codes-visibility-override'],
}

/** Renders an individual settings page based on the URL slug, validating feature flags before display. */
const SettingsPage: React.FC<{ match: { params: { page: string } } }> = ({
  match,
}) => {
  const page = match.params.page
  const merchant = useCurrentMerchant()

  // All flag hooks called unconditionally to satisfy rules of hooks
  const flagValues: Record<string, boolean> = {
    'mx-unavailable-item-visibility': useFlag(
      'mx-unavailable-item-visibility',
      false
    ),
    'delivery-optimization-mx': useFlag('delivery-optimization-mx', false),
    'mx-customizable-legal-docs': useFlag('mx-customizable-legal-docs', false),
    'instore-enrollment-settings': useFlag(
      'instore-enrollment-settings',
      false
    ),
    'ordering-error-message-customization': useFlag(
      'ordering-error-message-customization',
      false
    ),
    'mx-4-ordering-management': useFlag('mx-4-ordering-management', false),
    'toast-revenue-center-enabled-mx': useFlag(
      'toast-revenue-center-enabled-mx',
      false
    ),
    'olo-time-based-menu-visibility-mx': useFlag(
      'olo-time-based-menu-visibility-mx',
      false
    ),
    'promo-codes-visibility-override': useFlag(
      'promo-codes-visibility-override',
      false
    ),
  }

  const requiredFlags = featureFlagsByPage[page] || []
  const allFlagsEnabled = requiredFlags.every(f => flagValues[f])

  const Component = pageComponents[page]
  if (!Component) {
    // eslint-disable-next-line no-console
    console.warn(
      `[Settings] Unknown page slug: "${match.params.page}", redirecting to default`
    )
    return <Redirect to="/settings/ordering_configuration" />
  }

  if (requiredFlags.length > 0 && !allFlagsEnabled) {
    return <Redirect to="/settings/ordering_configuration" />
  }

  // TimeBasedMenuVisibility renders null without this capability, which would
  // otherwise leave this standalone route showing an empty page.
  if (
    page === 'time_based_menu_visibility' &&
    !merchant?.supports_time_based_menu_visibility
  ) {
    return <Redirect to="/settings/ordering_configuration" />
  }

  // Thanx does not allow merchants to disable thumbs-up/thumbs-down feedback
  // collection (AI-1197) — hide this page for providers where the toggle
  // only suppresses that prompt rather than the whole post-order email.
  if (
    page === 'post_order_feedback' &&
    isPostOrderFeedbackHidden(merchant?.ordering_provider)
  ) {
    return <Redirect to="/settings/ordering_configuration" />
  }

  const titleKey = pageTitleKeys[match.params.page]
  const pageTitle = titleKey ? settingsT(titleKey) : t('title')
  return (
    <PageHeader title={pageTitle} containerClassName="d-flex flex-column">
      <Helmet>
        <title>{t('page_title')}</title>
      </Helmet>
      <div className="grey-05-bkg border-top-1 pb-xxl grey-20-border flex-grow-1">
        <Container className="py-xl">
          <Component showTitle={false} />
        </Container>
      </div>
    </PageHeader>
  )
}

/** Root settings route handler. Redirects to legacy CMS settings when the new settings page flag is off. */
const Settings: React.FC = () => {
  const isAdmin = useIsAdmin()
  const customizableLegalDocsFlag = useFlag('mx-customizable-legal-docs', false)
  const isCustomizableRoutesEnabled = customizableLegalDocsFlag && isAdmin
  const settingsPageEnabled = useFlag('mx-unavailable-item-visibility', false)

  if (!settingsPageEnabled) {
    return <Redirect to="/brand_content#ordering_configuration" />
  }

  return (
    <div className="full-height grey-05-bkg">
      <Switch>
        {isCustomizableRoutesEnabled && (
          <ProtectedRoute
            redirect="/thanx_campaigns"
            resource="CMS"
            exact
            path="/settings/terms_and_policies_builder/:legal_doc_type"
            accessType="manage"
            component={TermsAndPoliciesBuilder}
          />
        )}
        <ProtectedRoute
          redirect="/thanx_campaigns"
          resource="CMS"
          exact
          path="/settings"
          component={() => <Redirect to="/settings/ordering_configuration" />}
        />
        {/* Reward-expiration settings moved to a tab on /rewards. Gate on
            MerchantSettings to match the destination tab's access requirement
            (and the Reward Settings nav link), not CMS. */}
        <ProtectedRoute
          redirect="/thanx_campaigns"
          resource="MerchantSettings"
          exact
          path="/settings/reward_expiration"
          component={() => <Redirect to="/rewards#settings" />}
        />
        <ProtectedRoute
          redirect="/thanx_campaigns"
          resource="CMS"
          path="/settings/:page"
          component={SettingsPage}
        />
      </Switch>
    </div>
  )
}

export default Settings
